import type { AdminStaySortBy, AdminStaySortDirection, AdminStayStatus } from '@/api/core/stays';
import { hasAsciiControlOrSpace, isOffsetDateTime, isUuid } from '@/lib/validation';

const STATUSES: readonly AdminStayStatus[] = [
  'PREPARING',
  'READY',
  'CHECKED_IN',
  'CHECKED_OUT',
  'CANCELLED',
  'NO_SHOW',
  'TERMINATED',
];
const SORT_FIELDS: readonly AdminStaySortBy[] = [
  'created_at',
  'start_at',
  'end_at',
  'status',
  'stay_id',
];
const KEYS = [
  'property_id',
  'status',
  'starts_at',
  'ends_at',
  'cursor',
  'sort_by',
  'sort_direction',
] as const;
const canonicalUuid = (value: string | null): value is string =>
  value !== null && isUuid(value) && value === value.toLowerCase();
const isSortDirection = (value: string | null): value is AdminStaySortDirection =>
  value === 'asc' || value === 'desc';

export function stayListState(params: URLSearchParams) {
  const propertyId = params.get('property_id');
  const rawStatus = params.get('status');
  const startsAt = params.get('starts_at');
  const endsAt = params.get('ends_at');
  const cursor = params.get('cursor');
  const rawSortBy = params.get('sort_by');
  const rawSortDirection = params.get('sort_direction');
  const sorted = rawSortBy !== null || rawSortDirection !== null;
  const valid =
    [...params.keys()].every((key) => KEYS.includes(key as (typeof KEYS)[number])) &&
    KEYS.every((key) => params.getAll(key).length <= 1) &&
    (propertyId === null || canonicalUuid(propertyId)) &&
    (rawStatus === null || STATUSES.includes(rawStatus as AdminStayStatus)) &&
    ((startsAt === null && endsAt === null) ||
      (startsAt !== null &&
        endsAt !== null &&
        isOffsetDateTime(startsAt) &&
        isOffsetDateTime(endsAt) &&
        Date.parse(startsAt) < Date.parse(endsAt))) &&
    ((rawSortBy === null && rawSortDirection === null) ||
      (SORT_FIELDS.includes(rawSortBy as AdminStaySortBy) && isSortDirection(rawSortDirection))) &&
    (cursor === null || (cursor.length > 0 && cursor.length <= (sorted ? 1024 : 256)));
  return {
    valid,
    propertyId,
    status: rawStatus as AdminStayStatus | null,
    startsAt,
    endsAt,
    cursor,
    sortBy:
      valid && SORT_FIELDS.includes(rawSortBy as AdminStaySortBy)
        ? (rawSortBy as AdminStaySortBy)
        : null,
    sortDirection: valid && isSortDirection(rawSortDirection) ? rawSortDirection : null,
  };
}

export function stayReturnTo(value: string | null): string {
  if (
    !value ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    hasAsciiControlOrSpace(value)
  )
    return '/stays';
  try {
    const url = new URL(value, 'https://internal.invalid');
    if (
      url.origin !== 'https://internal.invalid' ||
      url.pathname !== '/stays' ||
      url.hash ||
      !stayListState(url.searchParams).valid
    )
      return '/stays';
    return url.pathname + url.search;
  } catch {
    return '/stays';
  }
}

export function stayDetailState(params: URLSearchParams) {
  const returnTo = params.get('returnTo');
  return {
    valid:
      params.getAll('returnTo').length <= 1 &&
      [...params.keys()].every((key) => key === 'returnTo'),
    returnTo: stayReturnTo(returnTo),
  };
}

export function stayDetailHref(stayId: string, returnTo: string): string {
  return `/stays/${stayId}?${new URLSearchParams({ returnTo: stayReturnTo(returnTo) })}`;
}
